import { DragAndDrop } from "@progress/kendo-react-common";
import { Splitter, type SplitterPaneProps } from "@progress/kendo-react-layout";
import { useCallback, useEffect, useRef, useState } from "react";
import { useListScenariosQuery } from "../../../api/scenarioApi";
import { useAppDispatch } from "../../../app/hooks";
import ChatConversation from "../../../chat/chatConversation";
import { notify } from "../../../components/notify";
import { useIntegerQueryParam } from "../../../hooks/query/useQueryParam";
import EmptyScenarios from "../components/emptyScenarios";
import ScenarioComparison from "../components/scenarioComparison";
import ScenarioLibrary, { type ScenarioLibraryScenario } from "../components/scenarioLibrary";

const Scenarios: React.FC = () => {
    const dispatch = useAppDispatch();
    const [selectedConversationId, setSelectedConversationId] = useIntegerQueryParam("id");
    const {
        data,
        isError
    } = useListScenariosQuery({
        skip: 0,
        take: 1
    });

    useEffect((): void => {
        if (isError) {
            dispatch(notify("Unable to load scenarios.", "error"));
        }
    }, [dispatch, isError]);

    const [selectedScenarios, setSelectedScenarios] = useState<(ScenarioLibraryScenario | null)[]>([null, null, null]);
    const [draggingScenario, setDraggingScenario] = useState<ScenarioLibraryScenario | null>(null);
    const draggingScenarioRef = useRef<ScenarioLibraryScenario | null>(null);
    const dragCleanupTimeoutRef = useRef<number | null>(null);

    const selectedScenarioIds: number[] = selectedScenarios
        .filter((scenario): scenario is ScenarioLibraryScenario => scenario !== null)
        .map(scenario => scenario.id);

    useEffect(() => () => {
        if (dragCleanupTimeoutRef.current !== null) {
            window.clearTimeout(dragCleanupTimeoutRef.current);
        }
    }, []);

    const clearDraggingScenario = useCallback((): void => {
        draggingScenarioRef.current = null;
        setDraggingScenario(null);
    }, []);

    const handleScenarioDragStart = useCallback((scenario: ScenarioLibraryScenario): void => {
        if (dragCleanupTimeoutRef.current !== null) {
            window.clearTimeout(dragCleanupTimeoutRef.current);
            dragCleanupTimeoutRef.current = null;
        }

        draggingScenarioRef.current = scenario;
        setDraggingScenario(scenario);
    }, []);

    const handleScenarioDragEnd = useCallback((): void => {
        dragCleanupTimeoutRef.current = window.setTimeout(() => {
            clearDraggingScenario();
            dragCleanupTimeoutRef.current = null;
        }, 0);
    }, [clearDraggingScenario]);

    const handleScenarioDrop = useCallback((slotIndex: number): void => {
        const droppedScenario = draggingScenarioRef.current;

        if (!droppedScenario) {
            return;
        }

        setSelectedScenarios(currentScenarios => currentScenarios.map((scenario, index) => {
            if (index === slotIndex) {
                return droppedScenario;
            }

            if (scenario?.id === droppedScenario.id) {
                return null;
            }

            return scenario;
        }));
        clearDraggingScenario();
    }, [clearDraggingScenario]);

    const handleScenarioRemove = useCallback((slotIndex: number): void => {
        setSelectedScenarios(currentScenarios => currentScenarios.map((scenario, index) => index === slotIndex ? null : scenario));
    }, []);

    const [panes, setPanes] = useState<SplitterPaneProps[]>([
        {
            size: "30%",
            min: "240px",
            collapsible: true
        },
        {
            min: "320px",
            collapsible: false
        },
        {
            size: "20%",
            min: "220px",
            collapsible: true
        }
    ]);

    if (!data || data.data.length === 0) {
        return <EmptyScenarios />;
    }

    return (
        <DragAndDrop>
            <Splitter
                style={{ height: "100%", width: "100%" }}
                panes={panes}
                onChange={e => setPanes(e.newState)}
            >
                <div className="h-100 d-flex flex-column">
                    <ChatConversation
                        conversationId={selectedConversationId}
                        onConversationChanged={setSelectedConversationId}
                    />
                </div>

                <div className="h-100 d-flex flex-column">
                    <ScenarioComparison
                        selectedScenarios={selectedScenarios}
                        draggingScenario={draggingScenario}
                        onScenarioDrop={handleScenarioDrop}
                        onScenarioRemove={handleScenarioRemove}
                    />
                </div>

                <div className="h-100 d-flex flex-column">
                    <ScenarioLibrary
                        selectedScenarioIds={selectedScenarioIds}
                        draggingScenarioId={draggingScenario?.id ?? null}
                        onScenarioDragStart={handleScenarioDragStart}
                        onScenarioDragEnd={handleScenarioDragEnd}
                    />
                </div>
            </Splitter>
        </DragAndDrop>
    );
};

export default Scenarios;
